<template>
  <div class="main">
    <b-card>
      <b-card-body>
        <b-row class="mb-2">
          <b-col md="12" class="d-flex justify-content-end">
            <b-button variant="primary" @click="showAddVehicleModal">
              <feather-icon icon="PlusIcon" class="mr-50" />
              New Vehicle
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="2" sm="1" class="my-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">Per page</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            class="d-flex justify-content-end mb-2"
            style="position: relative; z-index: 1"
          >
            <b-button variant="outline-danger" @click="clearFilters">
              Clear Filters
            </b-button>
          </b-col>

          <b-col cols="12">
            <b-table
              hover
              responsive
              :items="filteredVehicles"
              :fields="vehicleFields"
              :per-page="perPage"
              :current-page="currentPage"
            >
              <!-- License Plate Filter -->
              <!-- License Plate Filter -->
              <template #head(licensePlate)="scope">
                <div class="d-flex flex-column">
                  <span>License Plate</span>
                  <b-form-input
                    v-model="filters.licensePlate"
                    placeholder="Search License Plate"
                    size="sm"
                  />
                </div>
              </template>

              <!-- Make Filter -->
              <template #head(make)="scope">
                <div class="d-flex flex-column">
                  <span>Vehicle Make</span>
                  <b-form-input
                    v-model="filters.make"
                    placeholder="Search Make"
                    size="sm"
                  />
                </div>
              </template>

              <template #head(color)="scope">
                <div class="d-flex flex-column">
                  <span>Vehicle Color</span>
                  <b-form-input
                    v-model="filters.color"
                    placeholder="Search Color"
                    size="sm"
                  />
                </div>
              </template>

              <!-- Model Filter -->
              <template #head(model)="scope">
                <div class="d-flex flex-column">
                  <span>Vehicle Model</span>
                  <b-form-input
                    v-model="filters.model"
                    placeholder="Search Model"
                    size="sm"
                  />
                </div>
              </template>

              <!-- Year Filter -->
              <template #head(year)="scope">
                <div class="d-flex flex-column">
                  <span>Vehicle Year</span>
                  <b-form-input
                    v-model="filters.year"
                    placeholder="Search Year"
                    size="sm"
                    type="number"
                  />
                </div>
              </template>

              <!-- Odometer Reading Filter -->
              <template #head(odometerReading)="scope">
                <div class="d-flex flex-column">
                  <span>Odometer Reading</span>
                  <b-form-input
                    v-model="filters.odometerReading"
                    placeholder="Search Odometer Reading"
                    size="sm"
                  />
                </div>
              </template>

              <!-- Next Maintenance Due Date Range Picker -->
              <template #head(nextMaintenanceDue)="scope">
                <div class="d-flex flex-column">
                  <span>Next Maintenance Due</span>
                  <DatePicker
                    v-model="filters.nextMaintenanceDueRange"
                    range
                    format="YYYY-MM-DD"
                    placeholder="Select date range"
                    style="width: 150px"
                  />
                </div>
              </template>

              <!-- Insurance Expiry Date Range Picker -->
              <template #head(insuranceExpiryDate)="scope">
                <div class="d-flex flex-column">
                  <span>Insurance Expiry Date</span>
                  <DatePicker
                    v-model="filters.insuranceExpiryDateRange"
                    type="date"
                    range
                    format="YYYY-MM-DD"
                    placeholder="Select date range"
                    style="width: 150px"
                  />
                </div>
              </template>
              <template #cell(gpsDeviceID)="data">
                {{ getDeviceName(data.item.gpsDeviceID) }}
              </template>

              <template v-slot:cell(insuranceExpiryDate)="data">
                {{ convertTimestampToDate(data.item.insuranceExpiryDate) }}
              </template>

              <template v-slot:cell(nextMaintenanceDue)="data">
                {{ convertTimestampToDate(data.item.nextMaintenanceDue) }}
              </template>

              <template v-slot:cell(odometerReading)="data">
                {{ data.item.odometerReading }}
              </template>

              <template #cell(actions)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  class="m-1"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item @click="showViewVehicleModal(data.item)">
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-1">View</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showEditVehicleModal(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-1">Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    @click="confirmDeleteVehicle(data.item.vehicleId)"
                  >
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-1">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <div class="d-flex justify-content-center mb-1">
              <div class="d-flex justify-content-center mb-1" v-if="loading">
                <b-spinner
                  variant="success"
                  label="Spinning"
                  style="width: 3rem; height: 3rem"
                ></b-spinner>
              </div>
            </div>
          </b-col>

          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
            <b-form-group>
              Showing
              {{ totalRows > 0 ? perPage * (currentPage - 1) + 1 : 0 }}
              to
              {{
                totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0
              }}
              of
              {{ totalRows > 0 ? totalRows : 0 }}
              entries
            </b-form-group>
          </b-col>
        </b-row>

        <!-- View Vehicle Modal -->
        <b-modal
          id="modal-view-vehicle"
          title="View Vehicle"
          ref="modalViewVehicle"
          hide-footer
          centered
        >
          <b-form-group label="License Plate" label-for="licensePlate">
            <b-form-input
              id="licensePlate"
              v-model="viewVehicle.licensePlate"
              readonly
            />
          </b-form-group>

          <b-form-group label="Make" label-for="make">
            <b-form-input id="make" v-model="viewVehicle.make" readonly />
          </b-form-group>

          <!-- Color -->
          <b-form-group label="Color" label-for="color">
            <b-form-input id="color" v-model="viewVehicle.color" readonly />
          </b-form-group>

          <!-- Model -->
          <b-form-group label="Model" label-for="model">
            <b-form-input id="model" v-model="viewVehicle.model" readonly />
          </b-form-group>

          <!-- Year -->
          <b-form-group label="Year" label-for="year">
            <b-form-input
              id="year"
              type="number"
              v-model="viewVehicle.year"
              readonly
            />
          </b-form-group>

          <!-- Odometer Reading -->
          <b-form-group label="Odometer Reading" label-for="odometerReading">
            <b-form-input
              id="odometerReading"
              type="number"
              v-model="viewVehicle.odometerReading"
              readonly
            />
          </b-form-group>

          <!-- Litres Consumed -->
          <b-form-group label="Litres Consumed" label-for="litresConsumed">
            <b-form-input
              id="litresConsumed"
              type="number"
              v-model="viewVehicle.litresConsumed"
              readonly
            />
          </b-form-group>

          <!-- Kilometers Travelled -->
          <b-form-group
            label="Kilometers Travelled"
            label-for="kilometersTravelled"
          >
            <b-form-input
              id="kilometersTravelled"
              type="number"
              v-model="viewVehicle.kilometersTravelled"
              readonly
            />
          </b-form-group>

          <!-- Last Maintenance Date -->
          <b-form-group
            label="Last Maintenance Date"
            label-for="lastMaintenanceDate"
          >
            <!-- <b-form-input id="lastMaintenanceDate"
              :value="viewVehicle.lastMaintenanceDate ? new Date(viewVehicle.lastMaintenanceDate).toLocaleString('en-US', { timeZone: 'UTC' }) : ''"
              readonly /> -->
            <b-form-datepicker
              id="lastMaintenanceDate"
              type="date"
              v-model="viewVehicle.lastMaintenanceDate"
              disabled
            />
          </b-form-group>

          <!-- Next Maintenance Due -->
          <b-form-group
            label="Next Maintenance Due"
            label-for="nextMaintenanceDue"
          >
            <!-- <b-form-input id="nextMaintenanceDue"
              :value="viewVehicle.nextMaintenanceDue ? new Date(viewVehicle.nextMaintenanceDue).toLocaleString('en-US', { timeZone: 'UTC' }) : ''"
              readonly /> -->
            <b-form-datepicker
              id="nextMaintenanceDue"
              type="date"
              v-model="viewVehicle.nextMaintenanceDue"
              disabled
            />
          </b-form-group>
          <!-- Insurance Policy Number -->
          <b-form-group
            label="Insurance Policy Number"
            label-for="insurancePolicyNumber"
          >
            <b-form-input
              id="insurancePolicyNumber"
              v-model="viewVehicle.insurancePolicyNumber"
              readonly
            />
          </b-form-group>

          <!-- Insurance Expiry Date -->
          <b-form-group
            label="Insurance Expiry Date"
            label-for="insuranceExpiryDate"
          >
            <!-- <b-form-input id="insuranceExpiryDate"
              :value="viewVehicle.insuranceExpiryDate ? new Date(viewVehicle.insuranceExpiryDate).toLocaleString('en-US', { timeZone: 'UTC' }) : ''"
              readonly /> -->
            <b-form-datepicker
              id="insuranceExpiryDate"
              type="date"
              v-model="viewVehicle.insuranceExpiryDate"
              disabled
            />
          </b-form-group>

          <!-- GPS Device ID -->
          <b-form-group label="GPS Device" label-for="gpsDeviceID">
            <b-form-input
              id="viewGPSDeviceID"
              :value="getDeviceName(viewVehicle.gpsDeviceID)"
              readonly
            />
          </b-form-group>
          <CustomFieldsDetails
            :AdditionalFields="viewVehicle.additionalFields"
            :ReadOnlyFields="true"
            :ModuleReference="'Vehicle'"
          >
          </CustomFieldsDetails>
        </b-modal>

        <!-- Add Vehicle Modal -->
        <b-modal
          id="modal-add-vehicle"
          title="Add New Vehicle"
          ref="addVehicleModal"
          @ok.prevent="addNewVehicle"
          hide-footer
          centered
        >
          <validation-observer ref="addVehicleForm" #default="{ invalid }">
            <b-form @submit.prevent="addNewVehicle">
              <!-- Change the method to addNewVehicle -->
              <!-- License Plate -->
              <b-form-group label="License Plate" label-for="licensePlate">
                <validation-provider
                  name="License Plate"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="licensePlate"
                    v-model="newVehicle.licensePlate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Make -->
              <b-form-group label="Make" label-for="make">
                <validation-provider
                  name="Make"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="make" v-model="newVehicle.make" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Color -->
              <b-form-group label="Color" label-for="color">
                <validation-provider name="Color" v-slot="{ errors }">
                  <b-form-input id="color" v-model="newVehicle.color" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Model -->
              <b-form-group label="Model" label-for="model">
                <validation-provider
                  name="Model"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="model" v-model="newVehicle.model" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Year -->
              <b-form-group label="Year" label-for="year">
                <validation-provider
                  name="Year"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="year"
                    type="number"
                    min="1900"
                    v-model="newVehicle.year"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Odometer Reading -->
              <b-form-group
                label="Odometer Reading"
                label-for="odometerReading"
              >
                <validation-provider
                  name="Odometer Reading"
                  rules="numeric|min:0"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="odometerReading"
                    type="number"
                    v-model="newVehicle.odometerReading"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('odometerReading')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Litres Consumed -->
              <b-form-group label="Litres Consumed" label-for="litresConsumed">
                <validation-provider
                  name="Litres Consumed"
                  rules="numeric|min:0"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="litresConsumed"
                    type="number"
                    v-model="newVehicle.litresConsumed"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('litresConsumed')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Kilometers Travelled -->
              <b-form-group
                label="Kilometers Travelled"
                label-for="kilometersTravelled"
              >
                <validation-provider
                  name="Kilometers Travelled"
                  rules="numeric|min:0"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="kilometersTravelled"
                    type="number"
                    v-model="newVehicle.kilometersTravelled"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('kilometersTravelled')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Last Maintenance Date -->
              <b-form-group
                label="Last Maintenance Date"
                label-for="lastMaintenanceDate"
              >
                <validation-provider
                  name="Last Maintenance Date"
                  :rules="{
                    dateCheck: { nextDate: newVehicle.nextMaintenanceDue },
                  }"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="lastMaintenanceDate"
                    type="date"
                    v-model="newVehicle.lastMaintenanceDate"
                    :max="currentDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Next Maintenance Due -->
              <b-form-group
                label="Next Maintenance Due"
                label-for="nextMaintenanceDue"
              >
                <validation-provider
                  name="Next Maintenance Due"
                  rules="futureDateCheck"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="nextMaintenanceDue"
                    type="date"
                    v-model="newVehicle.nextMaintenanceDue"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Insurance Policy Number -->
              <b-form-group
                label="Insurance Policy Number"
                label-for="insurancePolicyNumber"
              >
                <validation-provider
                  name="Insurance Policy Number"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="insurancePolicyNumber"
                    v-model="newVehicle.insurancePolicyNumber"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Insurance Expiry Date -->
              <b-form-group
                label="Insurance Expiry Date*"
                label-for="insuranceExpiryDate"
              >
                <validation-provider
                  name="Insurance Expiry Date"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="insuranceExpiryDate"
                    type="date"
                    :min="currentDate"
                    v-model="newVehicle.insuranceExpiryDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- GPS Device Serial ID -->
              <b-form-group label="GPS Device" label-for="gpsDeviceID">
                <validation-provider name="GPS Device" v-slot="{ errors }">
                  <b-form-select
                    id="gpsDeviceID"
                    v-model="newVehicle.gpsDeviceId"
                    :options="gpsDeviceOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <CustomFieldsDetails
                :AdditionalFields="newVehicle.additionalFields"
                :ModuleReference="'Vehicle'"
                @onchange="additionalFieldChanges"
              ></CustomFieldsDetails>

              <!-- Submit Button -->
              <b-button type="button" variant="primary" @click="addNewVehicle()"
                >Add Vehicle</b-button
              >
            </b-form>
          </validation-observer>
        </b-modal>

        <!-- Edit Vehicle Modal -->
        <b-modal
          id="modal-edit-vehicle"
          title="Edit Vehicle"
          ref="modalEditVehicle"
          hide-footer
          centered
        >
          <validation-observer
            ref="editVehicleForm"
            v-slot="{ invalid, validate }"
          >
            <b-form @submit.prevent>
              <!-- License Plate -->
              <b-form-group label="License Plate" label-for="editLicensePlate">
                <validation-provider
                  name="License Plate"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editLicensePlate"
                    v-model="editVehicle.licensePlate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Make -->
              <b-form-group label="Make" label-for="editMake">
                <validation-provider
                  name="Make"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="editMake" v-model="editVehicle.make" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Color -->
              <b-form-group label="Color" label-for="editColor">
                <validation-provider name="Color" v-slot="{ errors }">
                  <b-form-input id="editColor" v-model="editVehicle.color" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Model -->
              <b-form-group label="Model" label-for="editModel">
                <validation-provider
                  name="Model"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="editModel" v-model="editVehicle.model" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Year -->
              <b-form-group label="Year" label-for="editYear">
                <validation-provider
                  name="Year"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editYear"
                    type="number"
                    min="0"
                    v-model="editVehicle.year"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Odometer Reading -->
              <b-form-group
                label="Odometer Reading"
                label-for="editOdometerReading"
              >
                <validation-provider
                  name="Odometer Reading"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editOdometerReading"
                    type="number"
                    v-model="editVehicle.odometerReading"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('odometerReading')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Litres Consumed-->
              <b-form-group
                label="Litres Consumed"
                label-for="editLitresConsumed"
              >
                <validation-provider
                  name="LitresConsumed"
                  rules="numeric|min:0"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editLitresConsumed"
                    type="number"
                    v-model="editVehicle.litresConsumed"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('litresConsumed')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Kilometers Travelled-->
              <b-form-group
                label="Kilometers Travelled"
                label-for="editKilometersTravelled"
              >
                <validation-provider
                  name="KilometersTravelled"
                  rules="numeric|min:0"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editKilometersTravelled"
                    type="number"
                    v-model="editVehicle.kilometersTravelled"
                    min="0"
                    @keypress="allowOnlyNumbers($event)"
                    @input="sanitizeInput('kilometersTravelled')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Last Maintenance Date -->
              <b-form-group
                label="Last Maintenance Date"
                label-for="editLastMaintenanceDate"
              >
                <validation-provider
                  name="Last Maintenance Date"
                  v-slot="{ errors }"
                >
                  <b-form-datepicker
                    id="editLastMaintenanceDate"
                    type="date"
                    v-model="editVehicle.lastMaintenanceDate"
                    :max="currentDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Next Maintenance Due -->
              <b-form-group
                label="Next Maintenance Due"
                label-for="editNextMaintenanceDue"
              >
                <validation-provider
                  name="Next Maintenance Due"
                  v-slot="{ errors }"
                >
                  <b-form-datepicker
                    id="editNextMaintenanceDue"
                    type="date"
                    v-model="editVehicle.nextMaintenanceDue"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Insurance Policy Number -->
              <b-form-group
                label="Insurance Policy Number"
                label-for="editInsurancePolicyNumber"
              >
                <validation-provider
                  name="Insurance Policy Number"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="editInsurancePolicyNumber"
                    v-model="editVehicle.insurancePolicyNumber"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Insurance Expiry Date -->
              <b-form-group
                label="Insurance Expiry Date"
                label-for="editInsuranceExpiryDate"
              >
                <validation-provider
                  name="Insurance Expiry Date"
                  v-slot="{ errors }"
                >
                  <b-form-datepicker
                    id="editInsuranceExpiryDate"
                    type="date"
                    :min="currentDate"
                    v-model="editVehicle.insuranceExpiryDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- GPS Device Serial ID -->
              <b-form-group label="GPS Device" label-for="editGPSDeviceID">
                <validation-provider name="GPS Device" v-slot="{ errors }">
                  <b-form-select
                    id="editGPSDeviceID"
                    v-model="editVehicle.gpsDeviceID"
                    :options="gpsDeviceOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <CustomFieldsDetails
                :AdditionalFields="editVehicle.additionalFields"
                :ModuleReference="'Vehicle'"
                @onchange="additionalFieldChangesedit"
              ></CustomFieldsDetails>

              <!-- Submit Button -->
              <b-button
                type="submit"
                variant="primary"
                @click="updateVehicle()"
                :disabled="invalid"
                >Update Vehicle</b-button
              >
            </b-form>
          </validation-observer>
        </b-modal>
      </b-card-body>
    </b-card>
  </div>
</template>
<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import Swal from "sweetalert2";
import {
  BCard,
  BCardBody,
  BTable,
  BModal,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BFormDatepicker,
  BInputGroupAppend,
  BButton,
  BDropdown,
  BDropdownItem,
  BSpinner,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapGetters, mapActions } from "vuex";
import CustomFieldsDetails from "@/views/component/CustomFieldsDetails.vue";
import { extend } from "vee-validate";

extend("dateCheck", {
  params: ["nextDate"],
  validate(value, { nextDate }) {
    if (!value || !nextDate) return true;
    return new Date(value) <= new Date(nextDate);
  },
  message: "Last Maintenance Date cannot be later than Next Maintenance Due.",
});

extend("futureDateCheck", {
  validate(value) {
    if (!value) return true;
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  },
  message: "Next Maintenance Due must be today or a future date.",
});

export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BFormDatepicker,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    ValidationProvider,
    BSpinner,
    CustomFieldsDetails,
    DatePicker,
  },
  data() {
    return {
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      filterOn: [
        "licensePlate",
        "make",
        "color",
        "model",
        "year",
        "odometerReading",
        "actions",
      ],
      vehicleFields: [
        {
          key: "licensePlate",
          label: "License Plate",
          sortable: true,
          filter: true,
        },
        { key: "make", label: "Make", sortable: true },
        { key: "color", label: "Color", sortable: true },
        { key: "model", label: "Model", sortable: true },
        { key: "year", label: "Year", sortable: true },
        { key: "odometerReading", label: "Odometer Reading", sortable: true },
        {
          key: "nextMaintenanceDue",
          label: "Next Maintenance Due",
          sortable: true,
        },
        {
          key: "insuranceExpiryDate",
          label: "Insurance Expiry Date",
          sortable: true,
        },
        { key: "actions", label: "Actions" },
      ],
      newVehicle: {
        licensePlate: null,
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
        additionalFields: null,
      },
      viewVehicle: {
        licensePlate: null,
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
        additionalFields: null,
      },
      editVehicle: {
        licensePlate: null,
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
        additionalFields: null,
      },
      gpsDeviceOptions: [],
      filters: {
        licensePlate: "",
        make: "",
        color: "",
        model: "",
        year: "",
        odometerReading: "",
        nextMaintenanceDueRange: [null, null],
        insuranceExpiryDateRange: [null, null],
      },
      currentDate: new Date().toISOString().split("T")[0],
    };
  },
  computed: {
    filteredVehicles() {
      return this.vehicles.filter((vehicle) => {
        console.log("vehicles", this.vehicles);
        // Check License Plate
        const matchesLicensePlate =
          this.filters.licensePlate.trim() === "" ||
          vehicle.licensePlate
            .toLowerCase()
            .includes(this.filters.licensePlate.toLowerCase());

        // Check Make
        const matchesMake =
          this.filters.make.trim() === "" ||
          vehicle.make.toLowerCase().includes(this.filters.make.toLowerCase());

        // Check Color
        const matchesColor =
          this.filters.color.trim() === "" ||
          vehicle.color
            ?.toLowerCase()
            .includes(this.filters.color.toLowerCase());

        // Check Model
        const matchesModel =
          this.filters.model.trim() === "" ||
          vehicle.model
            .toLowerCase()
            .includes(this.filters.model.toLowerCase());

        // Check Year
        const matchesYear =
          this.filters.year.trim() === "" ||
          vehicle.year.toString().includes(this.filters.year.toString());

        // Check Odometer Reading
        const matchesOdometerReading =
          this.filters.odometerReading.trim() === "" ||
          // (vehicle.odometerReading === 0 ? '-' : vehicle.odometerReading.toString()).includes(this.filters.odometerReading.toString());
          vehicle.odometerReading
            .toString()
            .includes(this.filters.odometerReading.toString());

        // Check Next Maintenance Due Range
        const matchesNextMaintenanceDue =
          (!this.filters.nextMaintenanceDueRange[0] &&
            !this.filters.nextMaintenanceDueRange[1]) ||
          (vehicle.nextMaintenanceDue &&
            new Date(vehicle.nextMaintenanceDue) >=
              new Date(this.filters.nextMaintenanceDueRange[0]) &&
            new Date(vehicle.nextMaintenanceDue) <=
              new Date(this.filters.nextMaintenanceDueRange[1]));

        // Check Insurance Expiry Date Range
        const matchesInsuranceExpiryDate =
          (!this.filters.insuranceExpiryDateRange[0] &&
            !this.filters.insuranceExpiryDateRange[1]) ||
          (vehicle.insuranceExpiryDate &&
            new Date(vehicle.insuranceExpiryDate) >=
              new Date(this.filters.insuranceExpiryDateRange[0]) &&
            new Date(vehicle.insuranceExpiryDate) <=
              new Date(this.filters.insuranceExpiryDateRange[1]));

        // Combine all filters
        return (
          matchesLicensePlate &&
          matchesMake &&
          matchesColor &&
          matchesModel &&
          matchesYear &&
          matchesOdometerReading &&
          matchesNextMaintenanceDue &&
          matchesInsuranceExpiryDate
        );
      });
    },
    ...mapGetters("vehicleModule", {
      vehicles: "vehicles",
      loading: "loading",
    }),

    ...mapGetters("gpsModule", {
      gpsdevices: "gpsdevices",
    }),
  },
  methods: {
    allowOnlyNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;

      if (
        (charCode < 48 || charCode > 57) &&
        charCode !== 46 &&
        charCode !== 8
      ) {
        event.preventDefault();
      }

      if (charCode === 46 && event.target.value.includes(".")) {
        event.preventDefault();
      }
    },

    sanitizeInput(field) {
      let sanitizedValue = this.items[field].toString().replace(/[^0-9.]/g, "");

      const parts = sanitizedValue.split(".");
      if (parts.length > 2) {
        sanitizedValue = parts[0] + "." + parts[1];
      }

      this.items[field] = sanitizedValue ? parseFloat(sanitizedValue) : 0;
    },

    // itemChanged(item) {
    //   let selectedMaterial = this.itemOptions.filter((obj) => {
    //     return obj.id === item.id;
    //   });
    //   if (selectedMaterial.length > 0) {
    //     item.uom = selectedMaterial[0].defaultUomID ?? selectedMaterial[0].defaultUomId;
    //     item.cost =
    //       (selectedMaterial[0].purchasePrice ?? 0) == 0
    //         ? selectedMaterial[0].salePrice ?? 0
    //         : selectedMaterial[0].purchasePrice ?? 0;
    //     item.totalCost = ((item.qty ?? 0) * (item.cost ?? 0)).toFixed(2);
    //   }
    // },

    clearFilters() {
      this.filters = {
        licensePlate: "",
        make: "",
        color: "",
        model: "",
        year: "",
        odometerReading: "",
        nextMaintenanceDueRange: [null, null],
        insuranceExpiryDateRange: [null, null],
      };
    },
    ...mapActions("vehicleModule", [
      "getVehicleListAction",
      "removeVehicleAction",
      "addVehicleAction",
      "updateVehicleAction",
    ]),
    ...mapActions("gpsModule", ["getGpsDeviceListAction"]),

    additionalFieldChanges(customFields) {
      this.newVehicle.additionalFields = customFields;
    },

    additionalFieldChangesedit(customFields) {
      this.editVehicle.additionalFields = customFields;
    },

    showAddVehicleModal() {
      this.$refs.addVehicleModal.show();
    },

    convertTimestampToDate(timestamp) {
      if (timestamp === null) {
        return "-";
      }
      return new Date(timestamp).toLocaleDateString();
    },

    getOdormeter(reading) {
      if (reading === 0) {
        return "-";
      }
      return reading;
    },

    successAdd() {
      this.$swal({
        title: "Vehicle Added!",
        text: "You have successfully added a Vehicle!",
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    resetForm() {
      this.newVehicle = {
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
      };
      this.editVehicle = {
        licensePlate: null,
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
      };
      this.viewVehicle = {
        licensePlate: null,
        make: null,
        color: null,
        model: null,
        year: null,
        odometerReading: null,
        litresConsumed: null,
        kilometersTravelled: null,
        lastMaintenanceDate: null,
        nextMaintenanceDue: null,
        insurancePolicyNumber: null,
        insuranceExpiryDate: null,
        gpsDeviceID: null,
      };
    },

    async addNewVehicle() {
      this.loading = true;
      const isValid = await this.$refs.addVehicleForm.validate();
      if (isValid) {
        const loader = Swal.fire({
          title: "Processing...",
          text: "Please wait while the Vehicle is being added",
          allowOutsideClick: () => !Swal.isLoading(),
          showConfirmButton: false,
          showCancelButton: false,
          buttonsStyling: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        if (this.newVehicle.odometerReading == "") {
          this.newVehicle.odometerReading = 0;
        }

        // try {
        await this.addVehicleAction(this.newVehicle)
          .then(() => {
            loader.close();
            this.successAdd();
            this.$bvModal.hide("modal-add-vehicle");
            //this.success('Vehicle added successfully.');
            this.loading = false;
            this.getVehicleListAction().then(() => {
              this.totalRows = this.vehicles.length;
            });
            this.resetForm();
          })
          .catch((error) => {
            loader.close();
            this.error(error.response.data);
            this.loading = false;
            this.getVehicleListAction().then(() => {
              this.totalRows = this.vehicles.length;
            });
          });
      }
      // } finally {
      //   this.getVehicleListAction();
      //   this.loading = false;
      //   }
      // } else {
      //     this.loading = false;
      //   }
    },

    error(data) {
      let msg = null;
      if (data) msg = this.getErrorString(data);
      this.$swal({
        title: "Error!",
        html: msg ?? " Failed!",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    getErrorString(data) {
      let msg = null;
      if (data.errors) {
        let errors = data.errors;
        msg = "";
        msg += "";
        for (var prop in errors) {
          if (
            Object.prototype.hasOwnProperty.call(errors, prop) &&
            prop !== "request"
          ) {
            msg += "<p>";
            msg += prop.replace("$.", "");
            msg += ": ";
            msg += errors[prop].toString().replace("$.", "");
            msg += "</p>";
          }
        }
        msg += "</div>";
      } else if (data.message) {
        msg = "<div>";
        msg += "";
        msg += data.message;
        msg += "</div>";
      }
      return msg;
    },

    showViewVehicleModal(item) {
      this.viewVehicle = { ...item };
      this.$refs.modalViewVehicle.show();
    },
    showEditVehicleModal(item) {
      this.editVehicle = { ...item };
      this.$refs.modalEditVehicle.show();
      this.getVehicleListAction();
    },

    // successAdd() {
    //   this.$swal({
    //     title: "Vehicle Added!",
    //     text: "You have successfully added a Vehicle!",
    //     icon: "success",
    //     customClass: {
    //       confirmButton: "btn btn-primary",
    //     },
    //     buttonsStyling: false,
    //   });
    // },

    successEdit() {
      this.$swal({
        title: "Vehicle Updated!",
        text: "You have successfully updated a Vehicle!",
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    getDeviceName(id) {
      const device = this.gpsdevices.find((s) => s.gpsDeviceId === id);
      return device ? device.serialNumber : "-";
    },

    async updateVehicle() {
      this.loading = true;
      const loader = Swal.fire({
        title: "Processing...",
        text: "Please wait while the Vehicle is being updated",
        allowOutsideClick: () => !Swal.isLoading(),
        showConfirmButton: false,
        showCancelButton: false,
        buttonsStyling: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      if (this.editVehicle.odometerReading == "") {
        this.editVehicle.odometerReading = 0;
      }

      try {
        console.log("editvehicle", this.editVehicle);
        await this.updateVehicleAction(this.editVehicle);
        loader.close();
        this.$bvModal.hide("modal-edit-vehicle");
        this.successEdit();
        this.loading = false;
        this.resetForm();
        this.getVehicleListAction().then(() => {
          this.totalRows = this.vehicles.length;
        });
      } catch (error) {
        loader.close();
        this.error(error.response.data);
        this.loading = false;
        this.getVehicleListAction().then(() => {
          this.totalRows = this.vehicles.length;
        });
      } finally {
        this.getVehicleListAction().then(() => {
          this.totalRows = this.vehicles.length;
        });
        this.loading = false;
      }
    },

    async confirmDeleteVehicle(vehicleID) {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (confirmation.isConfirmed) {
        this.deleteVehicle(vehicleID);
      } else if (confirmation.dismiss === Swal.DismissReason.cancel) {
        Swal.fire({
          title: "Cancelled",
          text: "Your Vehicle is safe :)",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-success",
          },
        });
      }
    },

    async deleteVehicle(vehicleID) {
      const loader = Swal.fire({
        title: "Processing...",
        text: "Please wait while the Vehicle is being deleted",
        allowOutsideClick: () => !Swal.isLoading(),
        showConfirmButton: false,
        showCancelButton: false,
        buttonsStyling: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await this.removeVehicleAction(vehicleID);
        loader.close();
        Swal.fire({
          title: "Deleted!",
          text: "The Vehicle has been deleted.",
          icon: "success",
          customClass: {
            confirmButton: "btn btn-success",
          },
        });
        this.resetForm();
        this.getVehicleListAction();
      } catch (error) {
        loader.close();
        this.error(error.response.data);
        this.getVehicleListAction();
      } finally {
        this.loading = false;
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.getVehicleListAction().then(() => {
      console.log("vehicleslength", this.filteredVehicles);
      this.totalRows = this.vehicles.length;
    });
    await this.getGpsDeviceListAction().then(() => {
      // this.gpsdevices.map((v, i) =>
      //   this.gpsDeviceOptions.push({
      //     value: v.gpsDeviceId,
      //     text: `${v.serialNumber}`,
      //   })
      // );

      this.gpsdevices
        .filter((device) => device.status === "Active") // Only include active devices
        .forEach((v) => {
          this.gpsDeviceOptions.push({
            value: v.gpsDeviceId,
            text: `${v.serialNumber}`,
          });
        });
    });
  },
};
</script>
